import { useContext, useEffect } from "react"
import { XmppContext } from "@/stream/xmppClient"
import AdHocCommands, { UserPermissions } from "@/api/adHocCommands"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import {
  ASSIGN_DISPATCHERS,
  VIEW_DISPATCHERS,
  selectIsFleetManager,
  selectPermissions,
  selectPermissionsStale,
  setPermissions,
} from "@/reducers/permissionsSlice"

export { ASSIGN_DISPATCHERS, VIEW_DISPATCHERS }

// Module-level in-flight dedup: at most one network request at a time
let inFlight: Promise<UserPermissions> | null = null

export function useUserPermissions(): {
  permissions: string[]
  hasPermission: (permission: string) => boolean
  /** Sees every dispatcher's chats and hands vehicles out between them. */
  isFleetManager: boolean
  /** A dispatcher on a managed account: the manager decides which chats they
   * keep, so joining and leaving on their own is off. */
  membershipLocked: boolean
  loading: boolean
} {
  const { client } = useContext(XmppContext)
  const dispatch = useAppDispatch()
  const { permissions, accountManaged } = useAppSelector(selectPermissions)
  const isFleetManager = useAppSelector(selectIsFleetManager)
  const isStale = useAppSelector(selectPermissionsStale)

  useEffect(() => {
    if (!isStale) return

    if (!inFlight) {
      // Deferred so that a throw (no client yet, or a partial mock) ends up as
      // a rejection logged below instead of breaking the render.
      inFlight = Promise.resolve()
        .then(() => AdHocCommands.getPermissions(client))
        .finally(() => {
          inFlight = null
        })
    }

    inFlight
      .then((result) => {
        dispatch(setPermissions(result))
      })
      .catch((err) => {
        console.warn("Failed to fetch permissions", err)
      })
  }, [client, dispatch, isStale])

  const hasPermission = (permission: string) => permissions.includes(permission)

  return {
    permissions,
    hasPermission,
    isFleetManager,
    membershipLocked: accountManaged && !hasPermission(ASSIGN_DISPATCHERS),
    loading: isStale && inFlight !== null,
  }
}
